import React from "react";
import { Text } from "@chakra-ui/react";
import { FormikErrors, FormikProps, withFormik } from "formik";
import { Button } from "@chakra-ui/button";
import { Box, HStack, VStack } from "@chakra-ui/layout";
import { AdminApiClient } from "../api/adminApiClient";
import { CherryHubAdminApi } from "../api/adminApiModels";
import { TextField } from "../components/fields/TextField/TextField";
import { SwitchField } from "../components/fields/SwitchField/SwitchField";
import { isEmptyStr } from "../util/stringHelper";
import { FormStack } from "../components/FormStack/FormStack";
import { ChakraReactSelectField } from "../components/fields/SelectField/ChakraReactSelectField";
import * as Yup from "yup";
import { useAdminApiRequest } from "../hooks/useApiRequest";
import { AdminApiEnumConfigurations } from "../enums/AdminApiEnumConfigurations";
import { ApiResult } from "../api/dataApiClient";
import { CopyText } from "../components/Utilities/CopyText";

const emptyStrToNull = (value: string | null): string | null => {
  if (!value || value.trim() === "") {
    return null;
  } else {
    return value;
  }
};

interface IntegrationDetailFormValues {
  integrationName: string | null;
  enabled: boolean;
  enabledServices?: CherryHubAdminApi.ServiceType[] | null;
  integrationNo?: number;
  loyaltyConfigurationFromVenueId: string | null;
  recordOverrideIntegrationNo: number | null;
  sourceSystemName?: CherryHubAdminApi.SystemProvider;
  sourceSystemType?: CherryHubAdminApi.SystemType;
  systemImplementations?: CherryHubAdminApi.SystemImplementationType[] | null;
  linkedIntegrations: string[] | null;
}

interface IntegrationDetailFormProps {
  apiClient: AdminApiClient;
  businessId: string;
  integration: CherryHubAdminApi.Integration | null;
  isDisabled?: boolean;
  onCancel: () => void;
  onSuccess: (integration: CherryHubAdminApi.Integration) => void;
  onFailure: (message?: string) => void;
}

const InnerForm = ({
  isSubmitting,
  isDisabled,
  submitForm,
  isValid,
  onCancel,
  values,
  integration,
  businessId,
}: IntegrationDetailFormProps & FormikProps<IntegrationDetailFormValues>) => {
  const { data: venueOptions, isLoading: venueOptionsLoading } =
    useAdminApiRequest(
      (apiClient) => apiClient.getBusinessVenues(businessId, { limit: 1000 }),
      []
    );

  const { data: systemProviderOptions, isLoading: systemProviderLoading } =
    useAdminApiRequest(
      (apiClient) =>
        apiClient.getEnumConfigurations(
          AdminApiEnumConfigurations.SystemProvider
        ),
      []
    );

  const { data: systemTypeOptions, isLoading: systemTypeLoading } =
    useAdminApiRequest(
      (apiClient) =>
        apiClient.getEnumConfigurations(AdminApiEnumConfigurations.SystemType),
      []
    );

  const {
    data: systemImplementationTypeOptions,
    isLoading: systemImplementationTypeLoading,
  } = useAdminApiRequest(
    (apiClient) =>
      apiClient.getEnumConfigurations(
        AdminApiEnumConfigurations.SystemImplementationType
      ),
    []
  );

  const { data: serviceTypeOptions, isLoading: serviceTypeLoading } =
    useAdminApiRequest(
      (apiClient) =>
        apiClient.getEnumConfigurations(AdminApiEnumConfigurations.ServiceType),
      []
    );

  const { data: allIntegrations, isLoading: allIntegrationsLoading } =
    useAdminApiRequest(
      (apiClient) => apiClient.getBusinessIntegrations(businessId, {}),
      [businessId]
    );

  const linkedIntegrationOptions = (allIntegrations?.Results ?? [])
    .filter((item) => !integration || item.Meta?.Id !== integration?.Meta?.Id)
    .map((item) => ({
      label: `${item.DisplayName} (${item.Meta?.Id})`,
      value: item.Meta?.Id!,
    }));

  return (
    <>
      <Box width="100%" alignSelf="flex-start">
        <FormStack>
          {integration && (
            <HStack w="100%">
              <Box flexGrow={1} pb={4}>
                <CopyText text={integration?.Meta?.Id!} hover adjustMargin>
                  <Text fontSize="md" color="cherryUi.600">
                    {integration?.id} / {integration?.IntegrationNo}
                  </Text>
                </CopyText>
                <Text fontSize="xl" color="cherryUi.600">
                  <b>{integration?.IntegrationName}</b>
                </Text>
              </Box>

              <Box>
                <Button
                  colorScheme="cherryButton"
                  type="submit"
                  isLoading={isSubmitting}
                  disabled={isSubmitting || !isValid}
                  onClick={submitForm}
                >
                  Save
                </Button>
              </Box>

              <Box>
                <Button disabled={isSubmitting} onClick={onCancel}>
                  Cancel
                </Button>
              </Box>
            </HStack>
          )}

          <VStack w="100%" maxW="1200px" alignItems="flex-start" spacing={4}>
            <TextField
              horizontal
              valueProps={{ flex: 2 }}
              label="Integration Name"
              name="integrationName"
            />

            <SwitchField
              horizontal
              valueProps={{ flex: 2 }}
              label="Enabled"
              name="enabled"
            />

            <ChakraReactSelectField
              horizontal
              valueProps={{ flex: 2 }}
              label="Loyalty Configuration From Venue Id"
              name="loyaltyConfigurationFromVenueId"
              selectedOptionColor="blue"
              isLoading={venueOptionsLoading}
              allowEmpty="Please select.."
              options={
                venueOptions?.Results.map((venue) => {
                  return {
                    label: venue.DisplayName!,
                    value: venue.Meta!.Id!,
                  };
                }) || []
              }
            />

            <TextField
              horizontal
              valueProps={{ flex: 2 }}
              label="Record Override Integration No"
              name="recordOverrideIntegrationNo"
              type="number"
            />

            <ChakraReactSelectField
              horizontal
              valueProps={{ flex: 2 }}
              label="Source System Name"
              name="sourceSystemName"
              selectedOptionColor="blue"
              isLoading={systemProviderLoading}
              options={
                systemProviderOptions?.map((provider) => {
                  return {
                    label: provider.Value!,
                    value: provider.Key!,
                  };
                }) || []
              }
            />

            <ChakraReactSelectField
              horizontal
              valueProps={{ flex: 2 }}
              label="Source System Type"
              name="sourceSystemType"
              selectedOptionColor="blue"
              isLoading={systemTypeLoading}
              options={
                systemTypeOptions?.map((provider) => {
                  return {
                    label: provider.Value!,
                    value: provider.Key!,
                  };
                }) || []
              }
            />

            <ChakraReactSelectField
              horizontal
              valueProps={{ flex: 2 }}
              label="System Implementations"
              name="systemImplementations"
              selectedOptionColor="blue"
              isLoading={systemImplementationTypeLoading}
              isMulti
              options={
                systemImplementationTypeOptions?.map((provider) => {
                  return {
                    label: provider.Value!,
                    value: provider.Key!,
                  };
                }) || []
              }
            />

            <ChakraReactSelectField
              horizontal
              valueProps={{ flex: 2 }}
              label="Enabled Services"
              name="enabledServices"
              selectedOptionColor="blue"
              isLoading={serviceTypeLoading}
              isMulti
              options={
                serviceTypeOptions?.map((provider) => {
                  return {
                    label: provider.Value!,
                    value: provider.Key!,
                  };
                }) || []
              }
            />

            <ChakraReactSelectField
              horizontal
              valueProps={{ flex: 2 }}
              label="Linked Integrations"
              name="linkedIntegrations"
              selectedOptionColor="blue"
              isLoading={allIntegrationsLoading}
              isMulti
              options={linkedIntegrationOptions}
            />
          </VStack>

          <HStack width="100%" justifyContent="center" spacing="3" pt="8">
            <Button
              colorScheme="cherryButton"
              type="submit"
              isLoading={isSubmitting}
              disabled={isSubmitting || !isValid}
              onClick={submitForm}
            >
              Save
            </Button>
            <Button disabled={isSubmitting} onClick={onCancel}>
              Cancel
            </Button>
          </HStack>
        </FormStack>
      </Box>
    </>
  );
};

export const IntegrationDetailForm = withFormik<
  IntegrationDetailFormProps,
  IntegrationDetailFormValues
>({
  mapPropsToValues: ({ integration }) => {
    return {
      integrationName: integration?.IntegrationName ?? "",
      enabled: integration?.Enabled ?? true,
      enabledServices: integration?.EnabledServices || [],
      integrationNo: integration?.IntegrationNo,
      loyaltyConfigurationFromVenueId:
        integration?.LoyaltyConfigurationFromVenueId ?? "",
      recordOverrideIntegrationNo:
        integration == null || integration!.RecordOverrideIntegrationNo == null
          ? 0
          : integration?.RecordOverrideIntegrationNo,
      sourceSystemName: integration?.SourceSystemName,
      sourceSystemType: integration?.SourceSystemType,
      systemImplementations: integration?.SystemImplementations || [],
      linkedIntegrations: integration?.LinkedIntegrations ?? [],
    };
  },

  handleSubmit: async (values, { props, setFieldError }) => {
    if (isEmptyStr(values.integrationName)) {
      setFieldError("integrationName", "Integration name is required.");
      return;
    }

    // Translate form state to IntegrationUpdate
    const integrationDoc: Partial<CherryHubAdminApi.IntegrationUpdate> = {
      IntegrationName: values.integrationName,
      EnabledServices: values.enabledServices,
      LoyaltyConfigurationFromVenueId: emptyStrToNull(
        values.loyaltyConfigurationFromVenueId
      ),
      RecordOverrideIntegrationNo: values.recordOverrideIntegrationNo,
      Schedule: props.integration?.Schedule,
      SourceSystemName: values.sourceSystemName,
      SourceSystemType: values.sourceSystemType,
      SystemImplementations: values.systemImplementations,
      LinkedIntegrations: values.linkedIntegrations,
      Enabled: values.enabled,
      MembershipLoyaltyServiceConfiguration:
        props.integration?.MembershipLoyaltyServiceConfiguration,
      MembershipManagementServiceConfiguration:
        props.integration?.MembershipManagementServiceConfiguration,
    };

    let result: ApiResult<CherryHubAdminApi.Integration> | null = null;

    if (props.integration == null) {
      result = await props.apiClient.createBusinessIntegration(
        props.businessId,
        integrationDoc
      );
    } else {
      result = await props.apiClient.updateBusinessIntegration(
        props.businessId,
        props.integration.id,
        integrationDoc
      );
    }

    if (result.ok) {
      if (props.onSuccess) {
        props.onSuccess(result.data);
      }
    } else {
      if (props.onFailure) {
        props.onFailure(result.message);
      }
    }
  },

  validateOnBlur: false,
  validateOnMount: false,
  validateOnChange: true,

  validate: (values) => {
    let errors: FormikErrors<IntegrationDetailFormValues> = {};

    return errors;
  },
  validationSchema: Yup.object({
    smsSenderId: Yup.string().matches(/^[a-zA-Z0-9]{0,11}$/, {
      message:
        "Invalid SMS SenderId, only allow alphanumeric with maximum 11 characters",
      excludeEmptyString: false,
    }),
  }),
})(InnerForm);
